import { Injectable } from '@angular/core';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { Observable, of } from 'rxjs';
import { Action, select, Store } from '@ngrx/store';
import { catchError, filter, ignoreElements, map, mapTo, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { SearchHistoryUpdater } from './../../services/search-history.service';
import {
  AddSearchHistoryTerm,
  LoadSearchSuggestions,
  LoadSearchSuggestionsFailure,
  LoadSearchSuggestionsSuccess,
  RemoveSearchHistoryTerm,
  ResetSuggestions,
  SearchSuggestionsActionTypes,
  UpdateSearchHistoryTermsSuccess,
  ShowSuggestionsList,
} from './search-suggestions.actions';
import { SearchSuggestionsService } from '../../services/search-suggestions.service';
import { ErrorResponse } from '~shared/models/error-response.model';
import { HistoryTerm } from './../../models/history-term.model';
import * as fromBuyerSelectors from '../selectors';
import { BuyerState } from '../state';
import { StringUtil } from '~shared/utils/string.util';
import { SelectSuggestionByKeyboardService } from '~app/modules/generic/services/select-suggestion-by-keyboard.service';
import { SearchActionTypes } from '~app/modules/generic/store/search/search.actions';
import { NUMBER_0 } from '~shared/constants';

@Injectable({
  providedIn: 'root',
})
export class SearchSuggestionsEffects {
  constructor(
    private actions$: Actions,
    private store: Store<BuyerState>,
    private suggestionsService: SearchSuggestionsService,
    private historyUpdater: SearchHistoryUpdater,
    private selectSuggestionByKeyboardService: SelectSuggestionByKeyboardService
  ) {}

  @Effect()
  loadSearchSuggestion$: Observable<Action> = this.actions$.pipe(
    ofType(SearchSuggestionsActionTypes.LOAD_SEARCH_SUGGESTIONS),
    filter((action: LoadSearchSuggestions) => NUMBER_0 < action.payload.term.trim().length),
    map((action) => StringUtil.trimNotNecessarySpaces(action.payload.term)),
    switchMap((term: string) => {
      return this.suggestionsService.loadSearchSuggestions(term)
        .pipe(
          map(({ suggestions = [], products = [] }) => {
            return new LoadSearchSuggestionsSuccess({ term, suggestions, products });
          }),
          catchError((error: ErrorResponse) => of(new LoadSearchSuggestionsFailure({ error })))
        );
    })
  );

  @Effect()
  resetSuggestion$: Observable<Action> = this.actions$.pipe(
    ofType(SearchSuggestionsActionTypes.HIDE_SUGGESTIONS_LIST, SearchActionTypes.START_SEARCH),
    mapTo(new ResetSuggestions())
  );

  /* Suggestions list effects */

  @Effect()
  showSuggestionsListHttp: Observable<Action> = this.actions$.pipe(
    ofType(SearchSuggestionsActionTypes.LOAD_SEARCH_SUGGESTIONS_SUCCESS),
    withLatestFrom(
      this.store.pipe(select(fromBuyerSelectors.isSearchInputFocused)),
      ({ payload }: LoadSearchSuggestionsSuccess, isSearchInputFocused) => {
        return { suggestions: payload?.suggestions || [], isSearchInputFocused };
      }
    ),
    filter(({ suggestions, isSearchInputFocused }) => 0 < suggestions.length && isSearchInputFocused),
    mapTo(new ShowSuggestionsList())
  );

  @Effect()
  onHideSuggestionsList$: Observable<void> = this.actions$.pipe(
    ofType(SearchSuggestionsActionTypes.HIDE_SUGGESTIONS_LIST),
    tap(() => {
      this.selectSuggestionByKeyboardService.resetSsgSelected();
    }),
    ignoreElements()
  );

  /* History items effects */

  @Effect()
  saveHistoryTerm$: Observable<Action> = this.actions$.pipe(
    ofType(SearchSuggestionsActionTypes.ADD_SEARCH_HISTORY_TERM),
    withLatestFrom(
      this.store.pipe(select(fromBuyerSelectors.getSearchSuggestionsHistoryTerms)),
      ({ payload }: AddSearchHistoryTerm, history: HistoryTerm[]) => ({ term: payload, history })
    ),
    map(({ term, history }) => this.historyUpdater.saveHistoryTerm(term, history)),
    switchMap((nextHistory) => of(new UpdateSearchHistoryTermsSuccess(nextHistory)))
  );

  @Effect()
  removeHistoryTerm$: Observable<Action> = this.actions$.pipe(
    ofType(SearchSuggestionsActionTypes.REMOVE_SEARCH_HISTORY_TERM),
    withLatestFrom(
      this.store.pipe(select(fromBuyerSelectors.getSearchSuggestionsHistoryTerms)),
      ({ payload }: RemoveSearchHistoryTerm, history: HistoryTerm[]) => ({ term: payload, history })
    ),
    map(({ term, history }) => this.historyUpdater.removeHistoryTerm(term, history)),
    switchMap((nextHistory) => of(new UpdateSearchHistoryTermsSuccess(nextHistory)))
  );
}
